import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { helper } from 'src/environments/helper';
import { Observable, Subject, catchError, distinctUntilChanged, finalize, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { Generic } from 'src/app/interfaces/generic.interface';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { RequestsService } from '../requests.service';
import { UsersService } from '../../../settings-sidenav/users-settings/users.service';
import { UpdateMassAssignPayload } from '../request.interface';
import { NotificationService } from 'src/app/services/notification.service';

@Component({
    selector: 'app-assign-request',
    templateUrl: './assign-request.component.html',
    styleUrls: ['./assign-request.component.scss'],
})
export class AssignRequestComponent implements OnInit, OnDestroy {
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    isLoading = false;

    defaultParameter: { name: string; id: string } = {
        name: 'Toate',
        id: '-1',
    };

    private destroy$ = new Subject<void>();

    directiiActive: Generic[] = [];
    filteredDirectii: Generic[] = [];
    directiiControl = new FormControl();

    compartimenteActive: Generic[] = [];
    filteredCompartimente: Generic[] = [];
    compartimenteControl = new FormControl();

    usersActive: Generic[] = [];
    filteredUsers: Generic[] = [];
    usersControl = new FormControl();

    form: FormGroup = new FormGroup({
        directie_id: new FormControl('', [Validators.required]),
        compartiment_id: new FormControl(''),
        user_id: new FormControl(''),
    });

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            selectedRowIds: number[];
            directiiActive: Generic[]
        },
        private dialogRef: MatDialogRef<AssignRequestComponent>,
        private requestsService: RequestsService,
        private usersService: UsersService,
        private notificationService: NotificationService
    ) {
        console.log(this.data.directiiActive);
        this.directiiActive = this.data.directiiActive;
        this.filteredDirectii = [...this.data.directiiActive];
    }

    ngOnInit(): void {
        this.loadCompartimenteActive();
        this.loadUsersActive();

        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: Generic[]) => { this.filteredDirectii = data; }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: Generic[]) => { this.filteredCompartimente = data; }),
            catchError(() => of([]))
        ).subscribe();

        this.usersControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredUsers = this.usersActive;
                    return of(this.usersActive);
                }
                return this.filterOperatori(inputValue);
            }),
            tap((data: Generic[]) => { this.filteredUsers = data; }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<Generic[]> {
        const normalized = this.normalizeString(searchTerm);
        return of(this.directiiActive.filter((d) =>
            this.normalizeString(d.name).includes(normalized)
        ));
    }

    filterCompartimente(searchTerm: string): Observable<Generic[]> {
        const normalized = this.normalizeString(searchTerm);
        return of(this.compartimenteActive.filter((c) =>
            this.normalizeString(c.name).includes(normalized)
        ));
    }

    filterOperatori(searchTerm: string): Observable<Generic[]> {
        const normalized = this.normalizeString(searchTerm);
        return of(this.usersActive.filter((u) =>
            this.normalizeString(u.name).includes(normalized)
        ));
    }

    onDirectieClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.form.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.form.controls['compartiment_id'].reset();
        this.form.controls['user_id'].reset();

        if (directieId > 0) {
            this.loadCompartimenteActive(directieId);
            this.loadUsersActive();
        } else {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
            this.usersActive = [];
            this.filteredUsers = [];
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimente$ = directieId
            ? this.requestsService.getCompartimenteByDirectieId(directieId)
            : this.requestsService.getCompartimenteActive();

        compartimente$
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (data) => {
                    this.compartimenteActive = data;
                    this.filteredCompartimente = [...data];
                },
                error: (err) => {
                    console.error('Error loading compartimente', err);
                    this.filteredCompartimente = [];
                },
            });
    }

    onCompartimentClosed(): void {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(
                (compartiment) => compartiment.id === this.form.get('compartiment_id')?.value
            );
            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onCompartimentChange(compartimentId: number): void {
        this.form.controls['user_id'].reset();

        if (compartimentId > 0) {
            this.loadUsersActive();
        } else {
            this.usersActive = [];
            this.filteredUsers = [];
        }
    }

    loadUsersActive() {
        this.isLoading = true;
        this.usersService.getUsers()
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (data) => {
                    this.usersActive = data;
                    this.filteredUsers = [...data];
                },
                error: (err) => {
                    console.error('Error loading users', err);
                    this.filteredUsers = [];
                },
            });
    }

    onUsersClosed(): void {
        const selectedValue = this.usersControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredUsers.some(
                (user) => user.id === this.form.get('user_id')?.value
            );
            if (!isSelectedInFilteredList) {
                this.usersControl.setValue(null);
                this.filteredUsers = this.usersActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    submitForm() {
        this.isLoading = true;

        const ids_cerere = this.data.selectedRowIds;
        const directie_id = this.form.get('directie_id')?.value;
        const compartiment_id = this.form.get('compartiment_id')?.value;
        const user_id = this.form.get('user_id')?.value;

        const newAssign: UpdateMassAssignPayload = {
            ids_cerere,
            directie_id: directie_id,
            compartiment_id: compartiment_id,
            user_id: user_id,
        };

        this.requestsService
            .updateMassAssign(newAssign)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
